import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from 'icons';
import { LoginSignupModal } from 'mydnv/components/call-to-action-subscribe/login-signup-modal';
import { useSimpliCity } from 'cms/hooks/use-simplicity';
import { isArray, isValidGuid } from 'cms/utils/empty-exists';
import { Config } from 'config';
import * as bookmarkService from '../../../mydnv/services/bookmark-service';
import { getAccessToken } from 'mydnv/utils/access-token';
import { MyDNVContext } from 'mydnv/context/mydnv-context';

const BookmarkButton = (props) => {
    const { webpage, model } = useSimpliCity();
    const { title } = model || {};

    const { setActionType, wpObj } = props;

    const {
        account,
        emailVerified,
        bmTest,
        isAuthenticated,
        setBMTest,
        setBookmark,
        setShowBookmarksTip,
        instance,
        inProgress,
        InteractionStatusNone,
    } = useContext(MyDNVContext);
    const loginRequest = Config.MyDNV.loginRequest;

    const [bookmarked, setBookmarked] = useState(null);

    const request = useMemo(() => {
        return {
            ...loginRequest,
            account: account,
        };
    }, [account, loginRequest]);

    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    let webpageObject = useMemo(() => {
        if (wpObj !== undefined) {
            return wpObj;
        } else {
            return {
                guid: webpage.guid,
                title: title,
                published_date: webpage.created_at,
                updated_date: webpage.updated_at,
            };
        }
    }, [webpage, wpObj, title]);

    if (webpageObject === undefined && wpObj) {
        webpageObject = wpObj;
    }

    const bookmarkClicked = () => {
        if (isAuthenticated && !!account) {
            getAccessToken(
                request,
                instance,
                handleBookmark,
                { webpage: webpageObject },
                {
                    key: 'signin-redirect-action',
                    value: 'bookmark',
                }
            );
        } else {
            // remember user (not signed in) tried to bookmark current page
            localStorage.setItem('not-signedin-bookmark', JSON.stringify(webpage));
            localStorage.setItem('signin-redirect-action', 'bookmark');
            setIsLoginModalOpen(true);
        }
    };

    const handleModal = (isOpen) => {
        setIsLoginModalOpen(isOpen);

        if (!isOpen) {
            localStorage.removeItem('not-signedin-bookmark');
            localStorage.removeItem('signin-redirect-action');
        }
    };

    const handleBookmark = useCallback(async (args) => {
        try {
            await bookmarkService
                .postBookmark(args)
                .then((response) => {
                    if (response.status === 200) {
                        setBookmarked(true);
                        setActionType('bookmarked');
                        setBookmark(JSON.stringify(response.data));
                        setBMTest(response.data);
                        if (response.data.length === 1) {
                            setTimeout(() => {
                                setShowBookmarksTip(true);
                            }, 1);
                        }
                    }
                })
                .catch((err) => {
                    console.log(err);
                    if (err.response.status === 403) {
                        if (err.response.data === 'unverified user') {
                            // remember user (not signed in) tried to bookmark current page
                            localStorage.setItem('not-signedin-bookmark', JSON.stringify(args.webpage));
                            localStorage.setItem('signin-redirect-action', 'bookmark');
                        }
                    }
                    setActionType('error');
                });
        } catch (error) {
            console.log(error);
            setActionType('error');
        }
    }, []);

    const handleUnbookmark = useCallback(
        async (args) => {
            try {
                await bookmarkService
                    .deleteBookmark(args)
                    .then((response) => {
                        setBookmarked(false);
                        setActionType('unbookmarked');
                        setBookmark(JSON.stringify(response.data));
                        setBMTest(response.data);
                    })
                    .catch((err) => {
                        console.log(err);
                        setActionType('error');
                    });
            } catch (error) {
                console.log(error);
                setActionType('error');
            }
        },
        [setActionType]
    );

    useEffect(() => {
        // on page load, if user is being redirected from B2C login page
        if (isAuthenticated && inProgress === InteractionStatusNone && emailVerified && !!account) {
            if (localStorage.getItem('signin-redirect-action') === 'bookmark') {
                getAccessToken(
                    request,
                    instance,
                    handleBookmark,
                    { webpage: webpageObject },
                    {
                        key: 'signin-redirect-action',
                        value: 'bookmark',
                    }
                );
            } else if (localStorage.getItem('signin-redirect-action') === 'unbookmark') {
                getAccessToken(
                    request,
                    instance,
                    handleUnbookmark,
                    { webpage: webpageObject },
                    {
                        key: 'signin-redirect-action',
                        value: 'unbookmark',
                    }
                );
            }
        }
    }, [
        account,
        emailVerified,
        handleBookmark,
        handleUnbookmark,
        instance,
        isAuthenticated,
        inProgress,
        InteractionStatusNone,
        request,
        webpage,
        webpageObject,
    ]);

    useEffect(() => {
        const webpageGuid = webpageObject.guid;
        if (!!bmTest && isArray(bmTest) && isValidGuid(webpageGuid)) {
            const isBookmarkTest = (bookmark) => bookmark.guid === webpageGuid;
            setBookmarked(bmTest.findIndex(isBookmarkTest) >= 0);
        }
    }, [bmTest, webpageObject]);

    useEffect(() => {
        if (!!bookmarked && !isAuthenticated) {
            setBookmarked(false);
        }
    }, [isAuthenticated, bookmarked, setBookmarked]);

    return (
        <>
            {(!isAuthenticated || (!bookmarked && bookmarked !== null)) && (
                <button className='bookmark' onClick={bookmarkClicked}>
                    <span>Bookmark</span> <FontAwesomeIcon icon={regular.faBookmark} />
                    {/* <FontAwesomeIcon icon={solid.faStar} /> */}
                </button>
            )}

            {!!bookmarked && (
                <button
                    className='active bookmark'
                    onClick={() => {
                        getAccessToken(
                            request,
                            instance,
                            handleUnbookmark,
                            { webpage: webpageObject },
                            {
                                key: 'signin-redirect-action',
                                value: 'unbookmark',
                            },
                            () => {
                                setIsLoginModalOpen(true);
                            }
                        );
                    }}
                >
                    <span>Bookmarked</span> <FontAwesomeIcon icon={solid.faBookmark} />
                    {/* <FontAwesomeIcon icon={solid.faStar} /> */}
                </button>
            )}

            <LoginSignupModal isOpen={isLoginModalOpen} setModalOpen={handleModal} action={'bookmark'} />
        </>
    );
};

export { BookmarkButton };
